<template>
    <div class="redesign-export-manage">
        <div class="flex flex-col gap-3">
            <div class="w-full">
                <redesign-base-input
                    v-model="searchColumnInput"
                    prefix-icon="Search"
                    :placeholder="`${i18n.t('general.search')}`"
                />
            </div>
            <div class="w-auto flex-shrink-0">
                <div class="column-item">
                    <redesign-checkbox
                        :value="isAllCheckboxChecked"
                        :indeterminate="isAllIndeterminate"
                        hand-update
                        class="text-sm"
                        @update:checked="selectAllColumns"
                    >
                        {{ i18n.t('common.showAll') }}
                    </redesign-checkbox>
                </div>
            </div>
        </div>
        <div
            class="export-manage px-4 mb-4 py-4"
        >
            <div v-if="hiddenSearchedColumns.length">
                <div
                    v-for="(column, i) in hiddenSearchedColumns"
                    :key="column.prop + i"
                    class="column-item column-item--active"
                >
                    <redesign-checkbox
                        :label="column.label.fullTextLabel || column.label?.toString()"
                        class="text-sm"
                        :checked="false"
                        @update:checked="() => addToVisible(column)"
                    />
                </div>
            </div>
            <draggable
                v-model="visibleDraggableColumns"
                @end="onEnd"
            >
                <div
                    v-for="(column, i) in visibleSearched"
                    :key="column.prop + 'column' + i"
                    :class="{'column-item--active' : column.highlight }"
                    class="column-item"
                >
                    <redesign-checkbox
                        :label="column.label.fullTextLabel || column.label?.toString()"
                        :checked="true"
                        @update:checked="() => addToHidden(column)"
                    />
                    <redesign-base-icon :name="'GripVertical'" />
                </div>
            </draggable>
            <div v-if="hiddenUnSearchColumns">
                <div
                    v-for="(column, i) in hiddenUnSearchColumns"
                    :key="column.prop + i"
                    class="column-item"
                >
                    <redesign-checkbox
                        :label="column.label.fullTextLabel || column.label?.toString()"
                        :checked="false"
                        @update:checked="() => addToVisible(column)"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import i18n from '@/i18n.js'
import draggable from 'vuedraggable'
import RedesignBaseInput from '@/components/RedesignBaseComponents/Inputs/RedesignBaseInput.vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import RedesignCheckbox from '@/components/RedesignBaseComponents/Inputs/RedesignCheckbox.vue'
import { ColumnsToExport, WidgetTemplateColumnWithConfig } from '@/types/dashboard'

const searchColumnInput = ref('')

const props = defineProps<{
    visibleColumns: WidgetTemplateColumnWithConfig[]
    hiddenColumns: WidgetTemplateColumnWithConfig[]
    value: ColumnsToExport
}>()

const emit = defineEmits<{
    (event: 'input', payload: ColumnsToExport): void;
    (event: 'update:modelValue', payload: ColumnsToExport): void;
}>()

const visibleDraggableColumns = ref<WidgetTemplateColumnWithConfig[]>()

/* Computed */
const localColumnsToExport = computed<ColumnsToExport>({
    get () {
        return props.value
    },
    set (val) {
        emit('input', { ...val })
        emit('update:modelValue', { ...val })
    }
})

const isAllCheckboxChecked = computed(() => {
    return props.visibleColumns.length > 0 && !isAllIndeterminate.value
})
const isAllIndeterminate = computed(() => {
    return (props.visibleColumns.length > 0 && props.hiddenColumns.length > 0)
})
const visibleSearched = computed(() => {
    if (!searchColumnInput.value) {
        return [ ...props.visibleColumns ]
    }
    return [ ...props.visibleColumns ].map(col => {
        if (col.label.fullTextLabel.toLowerCase().includes(searchColumnInput.value.toLowerCase())) {
            return {
                ...col,
                highlight: true
            }
        }
        return col
    })
})

const hiddenSearchedColumns = computed(() => {
    if (!searchColumnInput.value) {
        return []
    }
    return [ ...props.hiddenColumns ].filter((column) => column.label.fullTextLabel.toLowerCase().includes(searchColumnInput.value.toLowerCase()))
})

const hiddenUnSearchColumns = computed(() => {
    if (!searchColumnInput.value) {
        return [ ...props.hiddenColumns ]
    }
    return [ ...props.hiddenColumns ].filter((column) => !column.label.fullTextLabel.toLowerCase().includes(searchColumnInput.value.toLowerCase()))
})

//Methods
function selectAllColumns (state?: string | number | boolean) {
    if (state) {
        showAll()
    } else {
        hideAll()
    }
}

const addToHidden = (column: WidgetTemplateColumnWithConfig) => {
    updateLocalColumnToExport(column.prop, false)
}

const addToVisible = (column: WidgetTemplateColumnWithConfig) => {
    updateLocalColumnToExport(column.prop, true)
}

function updateLocalColumnToExport (columnProp: string, value: boolean) {
    const _data: ColumnsToExport = { ...localColumnsToExport.value }
    if (!_data[columnProp]) {
        _data[columnProp] = {
            addToExport: value,
            sortOrder: Object.keys(_data).length
        }
    } else {
        _data[columnProp].addToExport = value
    }
    localColumnsToExport.value = { ..._data }
}

const onEnd = ({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }) => {
    if (oldIndex === newIndex) return

    const _data = { ...localColumnsToExport.value }
    visibleDraggableColumns.value?.forEach((column: WidgetTemplateColumnWithConfig, i) => {
        _data[column.prop].sortOrder = i
    })
    localColumnsToExport.value = { ..._data }
}

const showAll = () => {
    const _data = { ...localColumnsToExport.value }
    Object.keys(_data).forEach((key) => {
        _data[key].addToExport = true
    })
    localColumnsToExport.value = { ..._data }
}

const hideAll = () => {
    const _data = { ...localColumnsToExport.value }
    Object.keys(_data).forEach((key) => {
        _data[key].addToExport = false
    })
    localColumnsToExport.value = { ..._data }
}

defineExpose({
    clearData: () => {
        searchColumnInput.value = ''
    }
})

watch(() => props.visibleColumns, (newVal) => {
    visibleDraggableColumns.value = [ ...newVal ]
}, { immediate: true })
</script>

<style scoped lang="scss">
.redesign-export-manage {
    .export-manage {
        max-height: 390px;
        overflow-y: scroll;
        background-color: var(--secondaryBgColor-color);
    }

    .column-item {
        @apply p-2 flex justify-between items-center mb-2;
        border: 1px solid var(--gray-150);
        border-radius: 4px;

        &--active {
            border: 1px solid var(--primary-actions);
        }
    }
}

</style>
